import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Pencil, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";

export const SavedLinks = () => {
  const { api } = useOutletContext();
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ id: null, title: "", url: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/links").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { title: form.title, url: form.url };
      if (form.id) await api.put(`/admin/links/${form.id}`, body); else await api.post("/admin/links", body);
      toast.success(form.id ? "Link atualizado." : "Link guardado.");
      setForm({ id: null, title: "", url: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const del = async (l) => {
    if (!window.confirm(`Excluir o link “${l.title}”? Mensagens já enviadas não mudam.`)) return;
    try { await api.delete(`/admin/links/${l.id}`); toast.success("Link excluído."); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={save} className="space-y-3 rounded-md border border-zap-line p-4" data-testid="link-form">
        <h3 className="font-display text-base font-semibold">{form.id ? "Editar link" : "Guardar um link"}</h3>
        <p className="text-sm text-muted-foreground">Links guardados aparecem para escolher na conversa e nas transmissões, sem precisar colar de novo.</p>
        <Input data-testid="link-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Nome (ex.: Inscrição do encontro)" maxLength={80} required minLength={2} />
        <Input data-testid="link-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Endereço (ex.: iudp.org.br/inscricao)" maxLength={500} required />
        <div className="flex gap-2">
          <Button data-testid="link-save" disabled={busy}>{form.id ? "Salvar alterações" : "Guardar link"}</Button>
          {form.id && <Button type="button" variant="ghost" data-testid="link-cancel-edit" onClick={() => setForm({ id: null, title: "", url: "" })}>Cancelar</Button>}
        </div>
      </form>
      {list === null ? <p className="text-sm text-muted-foreground">Carregando…</p> : list.length === 0 ? (
        <p data-testid="links-empty" className="text-sm text-muted-foreground">Nenhum link guardado ainda.</p>
      ) : (
        <ul data-testid="links-list" className="divide-y divide-zap-line rounded-md border border-zap-line">
          {list.map((l) => (
            <li key={l.id} data-testid={`saved-link-${l.id}`} className="flex items-center gap-3 px-3 py-3">
              <Link2 className="h-5 w-5 shrink-0 text-zap-deep" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{l.title}</p>
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 truncate text-sm text-zap-deep underline-offset-2 hover:underline">
                  <span className="truncate">{l.url}</span><ExternalLink className="h-3.5 w-3.5 shrink-0" />
                </a>
              </div>
              <Button variant="ghost" size="sm" data-testid={`saved-link-edit-${l.id}`} onClick={() => setForm({ id: l.id, title: l.title, url: l.url })} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" data-testid={`saved-link-delete-${l.id}`} onClick={() => del(l)} className="text-red-700" aria-label="Excluir"><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
